<script lang="ts">
    import { createEventDispatcher, onMount, afterUpdate } from 'svelte';
    import { push } from 'svelte-spa-router';
    import Table from '../../Table.svelte';
    import SearchInput from '../../SearchInput.svelte';
    import Dropdown from '../../Dropdown.svelte';
    import Pagination from '../../Pagination.svelte';
    import StatusBadge from '../../StatusBadge.svelte';
    import type { DonationItem, PaginationInfo } from '../../../lib/api/employer/charity';
    import { statusOptions } from '../../../lib/utils/genericStatusOptions';

    export let donations: DonationItem[] = [];
    export let paging: PaginationInfo;
    export let currentPhrase: string = '';
    export let currentStatus: string = '';

    const dispatch = createEventDispatcher();

    // Search state - sync with prop
    let searchPhrase = currentPhrase;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    
    // Status filter state - sync with prop
    let selectedStatus = currentStatus || '';

    // Sync searchPhrase when currentPhrase prop changes (from parent)
    $: if (currentPhrase !== undefined && currentPhrase !== searchPhrase) {
        searchPhrase = currentPhrase;
    }
    
    // Sync selectedStatus when currentStatus prop changes (from parent)
    $: if (currentStatus !== undefined && currentStatus !== selectedStatus) {
        selectedStatus = currentStatus || '';
    }

    // Function to handle input events with debouncing
    function handleInput(event: CustomEvent<string>) {
        // Prevent any default behavior
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }
        
        const newPhrase = event.detail || '';
        searchPhrase = newPhrase;
        
        // Clear existing timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        
        // Debounce search (300ms delay)
        debounceTimer = setTimeout(() => {
            dispatch('updateFilters', {
                phrase: searchPhrase,
                page: 1
            });
        }, 300);
    }
    let filteredDonations: any[] = [];
    let error: string | null = null;

    // Function to transform donations data
    function transformDonations(donations: any[]) {
        if (!donations || donations.length === 0) {
            return [];
        }
        
        return donations.map((donation, index) => {
            // Try multiple possible UUID field names
            const uuid = donation.donation_request_uuid || 
                         donation.uuid || 
                         donation.id ||
                         donation.request_uuid;
            
            if (!uuid) {
                console.error(`Donation at index ${index} missing UUID:`, donation);
                console.error('Available keys:', Object.keys(donation));
            }
            
            return {
                uuid: uuid,
                id: uuid,
                employee_name: donation.employee_name || donation.user?.full_name || 'Unknown',
                employer_name: donation.employer_name || donation.employer?.name || 'N/A',
                leave_hours: donation.leave_hours,
                leave_value: donation.leave_value,
                employer_top_up: donation.employer_top_up,
                created_at: donation.created_at,
                approved_at: donation.approved_at,
                status: donation.status || donation.donation_request_status?.name || 'PENDING',
                employer_comment: donation.employer_comment
            };
        });
    }

    // Watch for changes in donations prop
    $: {
        filteredDonations = transformDonations(donations || []);
        // Don't set error for empty lists - let the UI show empty state instead
        error = null;
        console.log('Filtered donations:', filteredDonations);
        console.log('First donation uuid:', filteredDonations[0]?.uuid);
    }

    // Function to handle search button/Enter key (immediate search)
    function handleSearch(event: CustomEvent<string>) {
        const newPhrase = event.detail || '';
        searchPhrase = newPhrase;
        // Clear any pending timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        // Trigger search immediately
        dispatch('updateFilters', {
            phrase: searchPhrase,
            status: selectedStatus || undefined,
            page: 1
        });
    }
    
    // Function to handle status filter change
    function handleStatusChange(value: string) {
        selectedStatus = value;
        dispatch('updateFilters', {
            phrase: searchPhrase,
            status: selectedStatus || undefined,
            page: 1
        });
    }

    // Function to handle page change
    function handlePageChange(event: CustomEvent<{ page: number }>) {
        const requestedPage = event.detail.page;
        if (requestedPage > 0 && requestedPage <= (paging?.last_page || 1)) {
            dispatch('updateFilters', {
                phrase: searchPhrase,
                status: selectedStatus || undefined,
                page: requestedPage
            });
        }
    }

    // Function to handle date range change
    // function handleDateRangeChange(event: CustomEvent) {
    //     startDate = event.detail.startDate;
    //     endDate = event.detail.endDate;
    //     dispatch('updateFilters', {
    //         date_from: startDate,
    //         date_to: endDate,
    //         page: 1
    //     });
    // }

    // Helper functions for data formatting
    function formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value || 0);
    }

    function formatDate(dateString: string): string {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return dateString || '-';
        }
    }

    // Table columns definition
    const columns = [
        {
            key: 'employee_name',
            label: 'Employee',
            width: '15%',
            render: (value: string) => value || 'N/A'
        },
        {
            key: 'leave_hours',
            label: 'Hours',
            width: '10%',
            render: (value: number) => value ? `${value.toFixed(1)} hrs` : '0 hrs'
        },
        {
            key: 'leave_value',
            label: 'Leave Value',
            width: '12%',
            render: (value: number) => formatCurrency(value)
        },
        {
            key: 'employer_top_up',
            label: 'Employer Contribution',
            width: '12%',
            render: (value: number) => formatCurrency(value)
        },
        {
            key: 'approved_at',
            label: 'Approved',
            width: '12%',
            render: (value: string) => formatDate(value)
        },
        {
            key: 'status',
            label: 'Status',
            width: '14%',
            align: 'center',
            render: (value: string) => {
                return `<div class="status-badge-wrapper" data-status="${value}"></div>`;
            }
        },
        {
            key: 'employer_comment',
            label: 'Comment',
            width: '15%',
            render: (value: string | null) => {
                if (!value) return '-';
                return value.length > 50 ? `${value.substring(0, 47)}...` : value;
            }
        }
    ];

    // Add status badges to rendered table
    afterUpdate(() => {
        const statusWrappers = document.querySelectorAll('.status-badge-wrapper');
        statusWrappers.forEach((wrapper) => {
            const htmlWrapper = wrapper as HTMLElement;
            if (!wrapper.hasChildNodes()) {
                const status = wrapper.getAttribute('data-status');
                const statusBadge = document.createElement('div');
                htmlWrapper.appendChild(statusBadge);
                
                // Ensure status badge doesn't block row clicks
                htmlWrapper.style.pointerEvents = 'none';
                statusBadge.style.pointerEvents = 'none';
                
                // Create a new instance of StatusBadge component
                new StatusBadge({
                    target: statusBadge,
                    props: {
                        status: status ?? undefined
                    }
                });
            }
        });
    });

    // Handle row click to navigate to donation detail
    function handleRowClick(row: any) {
        console.log('Row clicked:', row);
        const uuid = row?.uuid || row?.id || row?.donation_request_uuid;
        if (uuid) {
            console.log('Navigating to donation:', uuid);
            push(`/view-leave-request?id=${uuid}`);
        } else {
            console.error('Row clicked but no uuid found:', row);
            console.error('Available keys:', row ? Object.keys(row) : 'row is null/undefined');
        }
    }

    onMount(() => {
        // Initial data transformation
        filteredDonations = transformDonations(donations);
    });
</script>

<div class="donations-list">
    <!-- Search and filters section -->
    <div class="filters">
        <SearchInput
            placeholder="Search by employee name..."
            value={searchPhrase}
            on:search={handleSearch}
            width="250px"
        />
        <Dropdown
            options={statusOptions}
            bind:selectedValue={selectedStatus}
            placeholder="Status"
            onSelectionChange={handleStatusChange}
        />
    </div>

    {#if error}
        <div class="error-message">
            {error}
        </div>
    {:else if filteredDonations.length > 0}
        <div class="table-container">
            <Table {columns} data={filteredDonations} onRowClick={handleRowClick} />
        </div>
    {:else}
        <div class="no-data">
            <p>No donations found</p>
        </div>
    {/if}

    {#if paging && paging.last_page > 1}
        <div class="pagination-container">
            <div class="pagination-info">
                Showing {paging.from} to {paging.to} of {paging.total} donations
            </div>
            <Pagination
                currentPage={(paging.current_page ?? 0) + 1}
                totalPages={paging.last_page || 1}
                maxVisiblePages={7}
                on:pageChange={handlePageChange}
            />
        </div>
    {:else if paging}
        <div class="pagination-info-only">
            Showing {paging.from} to {paging.to} of {paging.total} donations
        </div>
    {/if}
</div>

<style>
    .donations-list {
        padding: 1.5rem;
    }

    .filters {
        display: flex;
        gap: 1rem;
        margin-bottom: 1.5rem;
    }

    .table-container {
        background: white;
        border-radius: 0.5rem;
        overflow: hidden;
    }

    .pagination-container {
        margin-top: 1.5rem;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 1rem;
    }

    .pagination-info-only {
        margin-top: 1rem;
        text-align: right;
        color: #6B7280;
        font-size: 0.875rem;
    }

    .pagination-info {
        color: #6B7280;
        font-size: 0.875rem;
    }

    .error-message {
        text-align: center;
        padding: 2rem;
        color: #991B1B;
        background: #FEE2E2;
        border-radius: 0.5rem;
        margin-bottom: 1rem;
    }

    :global(.status-badge-wrapper) {
        display: inline-flex;
        align-items: center;
        pointer-events: none;
    }
    
    :global(.status-badge-wrapper *) {
        pointer-events: none;
    }

    :global(.status-badge) {
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
        text-transform: uppercase;
    }

    :global(.status-approved) {
        background-color: #DEF7EC;
        color: #03543F;
    }

    :global(.status-pending) {
        background-color: #FEF3C7;
        color: #92400E;
    }

    :global(.status-rejected) {
        background-color: #FEE2E2;
        color: #991B1B;
    }

    :global(.status-processed) {
        background-color: #E0E7FF;
        color: #3730A3;
    }
</style> 